import { isDefined } from "@clipboard-health/util-ts";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics/events";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { type Location } from "@src/appV2/lib/GoogleMaps";
import { useCurrentDate } from "@src/appV2/lib/utils/useCurrentDate";
import { convertToGeoLocation } from "@src/appV2/Location";
import { OpenGeoLocationBottomSheet } from "@src/appV2/redesign/OpenGeoLocation/OpenGeoLocationBottomSheet";
import { OpenGeoLocationProvider } from "@src/appV2/redesign/OpenGeoLocation/useOpenGeoLocation";
import { type ShiftStateData } from "@src/appV2/Shifts/Shift/ShiftState/types";
import { type Shift } from "@src/appV2/Shifts/Shift/types";
import { generatePath } from "react-router-dom";

import { SHIFT_DETAILS_OPEN_GEOLOCATION_FULL_PATH } from "../../../path";
import { useArrivalState } from "./hooks/useArrivalState";
import { useClockInContext } from "./hooks/useClockInContext";
import { useParkingInstructions } from "./hooks/useParkingInstructions";
import { useTravelTime } from "./hooks/useTravelTime";
import { ParkingCardContent } from "./ParkingCardContent";

export interface ParkingCardProps {
  shift: Pick<
    Shift,
    | "_id"
    | "start"
    | "facility"
    | "isSecondShiftInDouble"
    | "isAfterAnotherShift"
    | "agentReq"
    | "nfcTag"
    | "facilityId"
  >;
  workerId: string;
  shiftState: ShiftStateData;
}

export function ParkingCard(props: ParkingCardProps) {
  const { shift, workerId, shiftState } = props;
  const currentDate = useCurrentDate(1000);

  // 1. Determine arrival state (arrived, traveling, etc.)
  const arrivalContext = useArrivalState({
    shiftStart: shift.start,
    currentDate,
  });

  // 2. Calculate travel time if needed
  const { estimatedTravelTime, isLoading: isEstimatedTravelTimeLoading } = useTravelTime({
    arrivalContext,
    facilityCoordinates: shift.facility.geoLocation?.coordinates,
    workerId,
    shiftId: shift._id,
    workplaceId: shift.facilityId,
    shiftStartIso: shift.start,
  });

  // 3. Get clock in context (only when arrived)
  const clockInContext = useClockInContext({
    shiftStart: shift.start,
    shiftState,
    currentDate,
    arrivalState: arrivalContext.state,
    workerId,
    agentReq: shift.agentReq,
  });

  // 4. Get parking instructions
  const parkingContext = useParkingInstructions({
    facilityUserId: shift.facility.userId,
  });

  // 5. Analytics logging
  useLogEffect(
    APP_V2_USER_EVENTS.SHIFT_DETAILS_ASSUMED_JOURNEY_STATE_INSIDE_GEOFENCE,
    {
      shiftId: String(shift._id),
      workerId: String(workerId),
      facilityId: String(shift.facility.facilityId),
      deviceTimestamp: currentDate.toISOString(),
    },
    { enabled: arrivalContext.state === "arrived" }
  );

  useLogEffect(
    APP_V2_USER_EVENTS.SHIFT_DETAILS_ASSUMED_JOURNEY_STATE_UNKNOWN,
    {
      shiftId: String(shift._id),
      workerId: String(workerId),
      facilityId: String(shift.facility.facilityId),
      deviceTimestamp: currentDate.toISOString(),
    },
    { enabled: arrivalContext.state === "unknown" }
  );

  // 6. Setup location context for directions
  const facilityGeoLocation = isDefined(shift.facility.geoLocation?.coordinates)
    ? convertToGeoLocation(shift.facility.geoLocation.coordinates)
    : undefined;

  const location: Location = {
    lat: facilityGeoLocation?.latitude ?? 0,
    lng: facilityGeoLocation?.longitude ?? 0,
  };

  return (
    <OpenGeoLocationProvider
      location={location}
      name={shift.facility.name}
      formattedAddress={shift.facility.fullAddress?.formatted}
      bottomSheetRoutePath={generatePath(SHIFT_DETAILS_OPEN_GEOLOCATION_FULL_PATH, {
        shiftId: shift._id,
      })}
    >
      <ParkingCardContent
        arrivalContext={arrivalContext}
        clockInContext={clockInContext}
        parkingContext={parkingContext}
        estimatedTravelTime={estimatedTravelTime}
        isEstimatedTravelTimeLoading={isEstimatedTravelTimeLoading}
        shiftStart={shift.start}
        isNfcShift={isDefined(shift.nfcTag) && Boolean(shift.nfcTag)}
        workplaceTimezone={shift.facility.tmz ?? ""}
        currentDate={currentDate}
      />
      <OpenGeoLocationBottomSheet />
    </OpenGeoLocationProvider>
  );
}
